<script setup lang="ts">
import { XMarkIcon } from '@heroicons/vue/24/outline'
import { useI18n } from '@/composable/useI18n'

const props = withDefaults(
  defineProps<{
    loading?: boolean
    accentBar?: boolean
    legacyLayout?: boolean
  }>(),
  {
    loading: false,
    accentBar: true,
    legacyLayout: false,
  },
)

const emit = defineEmits<{
  cancel: []
}>()

const { t } = useI18n()

function handleCancel() {
  if (props.loading) return
  emit('cancel')
}
</script>

<template>
  <div class="widget-modal-root fixed inset-0 z-[9999] flex items-center justify-center px-4">
    <div
      class="widget-modal-backdrop absolute inset-0 bg-black/40"
      aria-hidden="true"
      @click="handleCancel"
    />

    <div
      class="widget-modal-panel relative flex w-full max-w-xl max-h-[90vh] flex-col bg-white shadow-xl"
      :class="legacyLayout ? 'p-10' : ''"
      role="dialog"
      aria-modal="true"
      @click.stop
    >
      <div v-if="accentBar && !legacyLayout" class="h-1 shrink-0 bg-blue" />

      <div
        :class="
          legacyLayout
            ? 'relative'
            : 'relative min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-8'
        "
      >
        <button
          type="button"
          class="absolute text-gray-400 transition-colors hover:text-black disabled:cursor-not-allowed disabled:opacity-40"
          :class="legacyLayout ? 'right-0 top-0' : 'right-6 top-7'"
          :disabled="loading"
          @click="handleCancel"
        >
          <XMarkIcon :class="legacyLayout ? 'h-6 w-6' : 'h-5 w-5'" />
        </button>

        <div
          v-if="loading"
          class="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-white/90"
        >
          <svg
            class="h-7 w-7 animate-spin text-blue"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            />
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <p class="text-sm font-medium text-body">
            {{ t('custom_dashboards.widget_modal.saving') }}
          </p>
        </div>

        <slot name="title" />
        <div :class="loading ? 'pointer-events-none select-none opacity-60' : ''">
          <slot />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.widget-modal-panel {
  transform-origin: center center;
}
</style>
